import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import api from "../utils/base_url";
import StockNotifyModal from "./StockNotifyModal";
import "../styles/index.css";
import "../styles/Combos.css";
import Footer from "./Footer";
import Navbar from "./Navbar";

// NOTE: adjust this to your actual combo-list endpoint if it differs.
const COMBO_LIST_URL = "combolist/";

// ── Cart item identity helpers ──────────────────────────────────────────
// Combo IDs and Product IDs are independent sequences (both start at 1), so
// a plain numeric id is NOT safe to use as the cart key — a combo #1 and a
// product #1 would collide into the same cart row. Every cart item carries
// a namespaced `id` ("combo-3") plus a `refId` (the real DB id) and
// `itemType`, used for navigation / API calls / checkout.
const cartKey = (id) => `combo-${id}`;

const toCartItem = (combo) => ({
  id: cartKey(combo.id),
  refId: combo.id,
  itemType: "combo",
  product_name: combo.combo_name,
  product_image: combo.combo_image,
  price: combo.combo_price,
  offer_price: null,
  original_price: combo.original_price,
});

const Combos = () => {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  const [notifyModal, setNotifyModal] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [notifyForm, setNotifyForm] = useState({ email: "", quantity: "" });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = useSelector((state) => state.handleCart);

  const fetchCombos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(COMBO_LIST_URL);
      setCombos(res.data.data || []);
    } catch (error) {
      console.error("Error fetching combos:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  // Restore scroll position when returning from a combo detail page,
  // same pattern as the Products page.
  useEffect(() => {
    if (loading || combos.length === 0) return;

    const savedY = sessionStorage.getItem("combos_scroll_y");
    if (!savedY) return;

    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: parseInt(savedY, 10), behavior: "instant" });
          sessionStorage.removeItem("combos_scroll_y");
        });
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, combos]);

  const getCartQty = (comboId) => {
    const item = state.find((p) => p.id === cartKey(comboId));
    return item ? item.qty : 0;
  };

  const addItem = (combo) => {
    const nextQty = getCartQty(combo.id) + 1;

    if (nextQty > combo.available_stock) {
      setStockError((prev) => ({ ...prev, [combo.id]: true }));
      setDisabledQty((prev) => ({ ...prev, [combo.id]: true }));

      setTimeout(() => {
        setStockError((prev) => ({ ...prev, [combo.id]: false }));
        setDisabledQty((prev) => ({ ...prev, [combo.id]: false }));
      }, 2000);

      return;
    }

    dispatch(addCart(toCartItem(combo)));
  };

  const removeItem = (combo) => {
    dispatch(delCart(toCartItem(combo)));

    setStockError((prev) => {
      const newState = { ...prev };
      delete newState[combo.id];
      return newState;
    });
  };

  const Loading = () => (
    <div className="grid-container">
      {[...Array(6)].map((_, idx) => (
        <div key={idx} className="combo-card skeleton-card skeleton-padding">
          <Skeleton height={140} className="skeleton-rounded" />
          <Skeleton height={20} width={120} className="skeleton-spacing-top" />
          <Skeleton height={12} width="90%" />
          <Skeleton height={12} width="85%" />
          <Skeleton height={20} width={90} className="skeleton-spacing-top" />
          <Skeleton height={35} width="60%" className="skeleton-center" />
        </div>
      ))}
    </div>
  );

  const RenderCombos = () => (
    <>
      <div className="grid-container">
        {combos.map((combo) => {
          const outOfStock = combo.available_stock === 0;
          const stockLow = combo.available_stock <= 2 && combo.available_stock > 0;

          return (
            <div
              key={combo.id}
              className="combo-card"
              onClick={() => {
                if (!outOfStock) {
                  sessionStorage.setItem("combos_scroll_y", window.scrollY);
                  navigate(`/combo/${combo.id}`);
                }
              }}
            >
              {outOfStock && (
                <span className="stock-badge out-stock">Out of Stock</span>
              )}
              {!outOfStock && stockLow && (
                <span className="stock-badge low-stock">
                  Hurry! Only {combo.available_stock} left
                </span>
              )}

              {stockError[combo.id] && (
                <motion.span
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: [0.8, 1.05, 1], opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="stock-error-badge"
                >
                  Stock limit reached!
                </motion.span>
              )}

              <div className="img-wrapper">
                <img
                  src={combo.combo_image}
                  alt={combo.combo_name}
                  loading="lazy"
                  decoding="async"
                  className="img-fallback-bg"
                />
              </div>

              <div className="combo-content">
                <div className="combo-title">
                  {combo.combo_name.length > 18
                    ? combo.combo_name.substring(0, 18) + "..."
                    : combo.combo_name}
                </div>
                <p className="combo-desc">{combo.description}</p>
                <div className="combo-price-wrap">
                  {combo.original_price &&
                    Number(combo.original_price) > Number(combo.combo_price) ? (
                    <>
                      <span className="combo-price-offer">
                        ₹ {parseFloat(combo.combo_price).toLocaleString()}
                      </span>
                      <span className="combo-price-original">
                        <s>₹ {parseFloat(combo.original_price).toLocaleString()}</s>
                      </span>
                    </>
                  ) : (
                    <span className="combo-price">
                      ₹ {parseFloat(combo.combo_price).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              <div className="combo-btns" onClick={(e) => e.stopPropagation()}>
                {outOfStock ? (
                  <div className="notify-wrap">
                    <button
                      className="notify-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNotifyProduct({ id: combo.id, product_name: combo.combo_name });
                        setNotifyForm({ email: "", quantity: "" });
                        setNotifyModal(true);
                      }}
                    >
                      <span className="notify-icon">🔔</span>
                      Notify Me
                    </button>
                  </div>
                ) : getCartQty(combo.id) === 0 ? (
                  <button className="btn-buy" onClick={() => addItem(combo)}>
                    Add to Cart
                  </button>
                ) : (
                  <div className="qty-box">
                    <button className="qty-btn" onClick={() => removeItem(combo)}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="qty-value">{getCartQty(combo.id)}</span>
                    <button
                      className="qty-btn"
                      onClick={() => addItem(combo)}
                      disabled={disabledQty[combo.id]}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}

              </div>
            </div>
          );
        })}
      </div>

      <StockNotifyModal
        open={notifyModal}
        onClose={() => setNotifyModal(false)}
        product={notifyProduct}
        notifyForm={notifyForm}
        setNotifyForm={setNotifyForm}
        navigate={navigate}
      />
    </>
  );

  return (
    <>
      <Navbar />
      <div className="container-fluid px-4 mt-4">
        <div className="combos-content">
          <h2 id="combo-title" className="display-6 fw-bold text-center text-uppercase mb-4">
            Our Combos
          </h2>
          {loading ? <Loading /> : <RenderCombos />}
          <div className="cp-bottom-back-wrap">
            <button
              onClick={() => navigate("/")}
              className="cp-back-btn"
            >
              ← Back Home
            </button>
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
};

export default Combos;